import { useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { TMR_MARK_PATHS, TmrMark } from '@tmr/design-system';
import type { Portal } from '../types';
import { getLoginKitMessages } from '../i18n';
import type { Locale } from '../i18n';
import { usePointerField, usePrefersReducedMotion } from './usePointerField';

/** Props for {@link LoginStage}. */
export interface LoginStageProps {
  /** The portal being signed into — its name, tagline, keyword and sub-line are the stage's copy. */
  portal: Portal;
  /** Turns the opening sequence and the pointer effects on or off. Reduced motion turns them off too. */
  motionOn?: boolean;
  /** Interface language. */
  locale?: Locale;
}

/** The opening plays once per browser session: a reload or a failed sign-in should not replay it. */
const INTRO_SEEN_KEY = 'tmr.login.stage-intro';
/** When the stage starts revealing under the fading opening, and when the opening leaves the DOM. */
const REVEAL_AT_MS = 1400;
const INTRO_DONE_MS = 2300;

/**
 * - `intro`  — the opening covers the stage; the stage's content waits, hidden.
 * - `reveal` — the opening bursts and fades while the stage's content rises in underneath it.
 * - `ready`  — the opening is gone.
 */
type StagePhase = 'intro' | 'reveal' | 'ready';

function introAlreadySeen(): boolean {
  try {
    return window.sessionStorage.getItem(INTRO_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markIntroSeen(): void {
  try {
    window.sessionStorage.setItem(INTRO_SEEN_KEY, '1');
  } catch {
    // Not remembering it only means the opening plays again next time.
  }
}

/**
 * The "stage" brand side of the login — the public site's hero turned into the door of the portal.
 *
 * A dark full-bleed stage with the site's blue glow following the pointer; the TMR mark drawn in light,
 * tilting in 3D inside the hero's orbits; glass cards with the site's claims floating at different
 * depths; the portal's tagline revealed word by word in the display face, its keyword as a giant outline
 * the glow lights up; and a running band of what the platform does. It opens with a short sequence (the
 * mark drawing itself) once per session.
 *
 * Everything is decoration around the same form: it only replaces {@link BrandCanvas} when
 * `LoginPage` gets `variant="stage"`. With reduced motion the final frame renders directly.
 *
 * @param props The portal, the motion switch and the language.
 */
export function LoginStage({ portal, motionOn = true, locale = 'pt-BR' }: LoginStageProps) {
  const t = getLoginKitMessages(locale);
  const reduced = usePrefersReducedMotion();
  const animate = motionOn && !reduced;

  const root = useRef<HTMLDivElement>(null);
  usePointerField(root, animate);

  const [phase, setPhase] = useState<StagePhase>(() =>
    animate && typeof window !== 'undefined' && !introAlreadySeen() ? 'intro' : 'ready',
  );

  // Both timers start on mount, alongside the opening's CSS animations; the reveal overlaps the fade-out
  // on purpose, so the stage is already rising when the curtain lifts.
  const playsIntro = useRef(phase === 'intro');
  useEffect(() => {
    if (!playsIntro.current) return;
    markIntroSeen();
    const reveal = window.setTimeout(() => setPhase((p) => (p === 'intro' ? 'reveal' : p)), REVEAL_AT_MS);
    const done = window.setTimeout(() => setPhase('ready'), INTRO_DONE_MS);
    return () => {
      window.clearTimeout(reveal);
      window.clearTimeout(done);
    };
  }, []);

  const gradientId = `stage-mark-${useId().replace(/:/g, '')}`;

  // One running index across every line, so the words light up in reading order.
  let wordIndex = 0;
  const lines = portal.tagline.map((line) => line.split(/\s+/).map((word) => ({ word, i: wordIndex++ })));

  return (
    <div ref={root} className="stage" data-phase={phase} data-motion={animate ? 'on' : 'off'}>
      <div className="stage-glow" aria-hidden="true" />
      <div className="stage-dots" aria-hidden="true" />
      <div className="stage-keyword" aria-hidden="true">
        {portal.keyword}
      </div>

      <div className="stage-top">
        {/* The symbol alone: the form card right beside it already names the portal. */}
        <TmrMark className="stage-logo" />
      </div>

      <div className="stage-copy">
        <h1 className="stage-headline">
          {lines.map((words, lineIndex) => (
            <span
              key={lineIndex}
              className={`stage-line ${lineIndex === lines.length - 1 ? 'stage-line--accent' : ''}`}
            >
              {words.map(({ word, i }) => (
                <span key={i} className="stage-word" style={{ '--i': i } as CSSProperties}>
                  {word}{' '}
                </span>
              ))}
            </span>
          ))}
        </h1>
        <p className="stage-sub">{portal.sub}</p>
      </div>

      <div className="stage-scene" aria-hidden="true">
        <div className="stage-orbit">
          <span className="stage-orbit-ring" />
          <span className="stage-orbit-dash" />
          <span className="stage-orbit-spark" />
        </div>
        <div className="stage-mark">
          <svg viewBox="0 0 74 53" fill="none">
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#959fff" />
                <stop offset="45%" stopColor="#1106f7" />
                <stop offset="100%" stopColor="#0195e4" />
              </linearGradient>
            </defs>
            {TMR_MARK_PATHS.map((d) => (
              <path key={d.slice(0, 16)} d={d} fill={`url(#${gradientId})`} />
            ))}
          </svg>
        </div>
        {t.stage.chips.map((chip, index) => (
          <div key={chip.title} className={`stage-chip stage-chip--${index}`}>
            <span className={`stage-chip-icon stage-chip-icon--${index}`}>{['✓', 'R$', '3/12'][index]}</span>
            <span>
              <strong>{chip.title}</strong>
              <small>{chip.detail}</small>
            </span>
          </div>
        ))}
      </div>

      <div className="stage-marquee" aria-hidden="true">
        <div className="stage-marquee-track">
          {[0, 1].map((copy) =>
            t.stage.marquee.map((item) => (
              <span key={`${copy}-${item}`} className="stage-marquee-item">
                {item}
              </span>
            )),
          )}
        </div>
      </div>

      {phase !== 'ready' && (
        <div className="stage-intro">
          <svg className="stage-intro-mark" viewBox="0 0 74 53" fill="none" aria-hidden="true">
            {TMR_MARK_PATHS.map((d) => (
              <path key={d.slice(0, 16)} d={d} pathLength={1} />
            ))}
          </svg>
          <button type="button" className="stage-intro-skip" onClick={() => setPhase('ready')}>
            {t.stage.skipIntro}
          </button>
        </div>
      )}
    </div>
  );
}
